import { useState } from 'react';
import { useParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Entitlements, PRODUCT_KEYS } from '@tips-sso/shared';
import { PageHeader } from '../components/PageHeader.js';
import { CustomerTabs } from '../components/CustomerTabs.js';
import { DataTable, type Column } from '../components/DataTable.js';
import { Modal } from '../components/Modal.js';
import { Field } from '../components/Field.js';
import { errorMessage } from '../api/client.js';
import {
  fetchEntitlements,
  grantEntitlement,
  type Entitlement,
  type GrantEntitlementInput,
} from '../api/endpoints.js';

const PRODUCTS = Object.values(PRODUCT_KEYS);
const ENTITLEMENT_KEYS = Object.values(Entitlements);

export function CustomerEntitlementsPage() {
  const { id: customerOrgId = '' } = useParams();
  const qc = useQueryClient();
  const [open, setOpen] = useState(false);

  const ents = useQuery({
    queryKey: ['entitlements', customerOrgId],
    queryFn: () => fetchEntitlements(customerOrgId),
  });

  const columns: Column<Entitlement>[] = [
    { key: 'product', header: 'Product', render: (e) => <span className="badge">{e.productKey}</span> },
    { key: 'key', header: 'Entitlement', render: (e) => e.entitlementKey },
    { key: 'value', header: 'Value', render: (e) => <code>{e.entitlementValue}</code> },
  ];

  return (
    <div className="page">
      <PageHeader
        title="Entitlements"
        subtitle={`Org ${customerOrgId}`}
        actions={
          <button type="button" className="btn btn--primary" onClick={() => setOpen(true)}>
            Grant Entitlement
          </button>
        }
      />
      <CustomerTabs customerOrgId={customerOrgId} />

      <DataTable
        columns={columns}
        rows={ents.data}
        rowKey={(e) => `${e.productKey}:${e.entitlementKey}`}
        isLoading={ents.isLoading}
        isError={ents.isError}
        error={ents.error}
        onRetry={() => void ents.refetch()}
        emptyMessage="No active entitlements."
      />

      <GrantModal
        customerOrgId={customerOrgId}
        open={open}
        onClose={() => setOpen(false)}
        onGranted={() => {
          setOpen(false);
          void qc.invalidateQueries({ queryKey: ['entitlements', customerOrgId] });
        }}
      />
    </div>
  );
}

function GrantModal({
  customerOrgId,
  open,
  onClose,
  onGranted,
}: {
  customerOrgId: string;
  open: boolean;
  onClose: () => void;
  onGranted: () => void;
}) {
  const [form, setForm] = useState<GrantEntitlementInput>({
    productKey: PRODUCT_KEYS.TipsData,
    entitlementKey: Entitlements.AggregateQueries,
    entitlementValue: 'true',
  });
  const mutation = useMutation({
    mutationFn: () => grantEntitlement(customerOrgId, form),
    onSuccess: onGranted,
  });

  return (
    <Modal open={open} title="Grant Entitlement" onClose={onClose}>
      <form
        className="form"
        onSubmit={(e) => {
          e.preventDefault();
          mutation.mutate();
        }}
      >
        {mutation.isError && <div className="form-error">{errorMessage(mutation.error)}</div>}
        <Field label="Product">
          <select
            className="field__select"
            value={form.productKey}
            onChange={(e) => setForm({ ...form, productKey: e.target.value })}
          >
            {PRODUCTS.map((p) => (
              <option key={p} value={p}>
                {p}
              </option>
            ))}
          </select>
        </Field>
        <Field label="Entitlement key">
          <select
            className="field__select"
            value={form.entitlementKey}
            onChange={(e) => setForm({ ...form, entitlementKey: e.target.value })}
          >
            {ENTITLEMENT_KEYS.map((k) => (
              <option key={k} value={k}>
                {k}
              </option>
            ))}
          </select>
        </Field>
        <Field label="Value" hint="e.g. true, or a numeric limit">
          <input
            className="field__input"
            value={form.entitlementValue}
            required
            onChange={(e) => setForm({ ...form, entitlementValue: e.target.value })}
          />
        </Field>
        <Field label="Ends at" hint="Optional ISO date; blank = no expiry">
          <input
            className="field__input"
            type="date"
            value={form.endsAt ?? ''}
            onChange={(e) => setForm({ ...form, endsAt: e.target.value || null })}
          />
        </Field>
        <div className="form__actions">
          <button type="button" className="btn btn--ghost" onClick={onClose}>
            Cancel
          </button>
          <button type="submit" className="btn btn--primary" disabled={mutation.isPending}>
            {mutation.isPending ? 'Granting…' : 'Grant'}
          </button>
        </div>
      </form>
    </Modal>
  );
}
